import { makeAutoObservable, computed } from 'mobx'
import { BrandConfig } from '@/Ship/Configs/BrandConfig'
import { themeManager } from '@/Ship/Stores/ThemeManager'
import type { AttachmentType } from '../Contracts'

/** Чат поверх ресурсних дроверів antd (їхній дефолт 1000) */
export const CHAT_Z_FRONT = 1001
/** Чат ПІД ресурсним дровером — коли з чату відкрили картку заявки */
export const CHAT_Z_BEHIND = 999

export interface PendingAttachment {
  type: AttachmentType
  file: File
  objectUrl: string
}

export class UIStore {
  isDrawerOpen = false
  activeChatId: string | null = null
  isMessageSearchOpen = false
  messageSearchQuery = ''
  activeSearchIndex = 0
  isChatInfoOpen = false
  replyToMessageId: string | null = null
  editingMessageId: string | null = null
  scrollToMessageId: string | null = null
  /** Основний колір бренду — той самий, що `colorPrimary` в `AntdConfig` */
  primaryColor = BrandConfig.color
  windowWidth = window.innerWidth
  pendingAttachment: PendingAttachment | null = null
  drafts: Map<string, string> = new Map()
  /**
   * Віконна логіка «останній відкритий — зверху»: відкриття заявки З чату
   * опускає чат під неї, будь-яка взаємодія з чатом піднімає його назад.
   */
  chatDrawerZIndex = CHAT_Z_FRONT

  constructor() {
    makeAutoObservable(this, {
      isMobile: computed,
      isTablet: computed,
      isCompact: computed,
    })
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth
    })
  }

  get isMobile() {
    return this.windowWidth < 768
  }

  get isTablet() {
    return this.windowWidth >= 768 && this.windowWidth < 1024
  }

  get isCompact() {
    return this.windowWidth < 1024
  }

  /** Тема — спільна для застосунку (перемикач у шапці); чат своєї не тримає */
  get isDarkMode() {
    return themeManager.isDark
  }

  openDrawer() {
    this.isDrawerOpen = true
    this.bringChatToFront()
  }

  /** Клік/відкриття чату — чат поверх ресурсних дроверів */
  bringChatToFront() {
    this.chatDrawerZIndex = CHAT_Z_FRONT
  }

  /** З чату відкрили картку сутності — вона має лягти поверх чату */
  sendChatBehindResources() {
    this.chatDrawerZIndex = CHAT_Z_BEHIND
  }

  closeDrawer() {
    this.isDrawerOpen = false
  }

  setActiveChatId(id: string | null) {
    this.activeChatId = id
    this.closeMessageSearch()
    this.isChatInfoOpen = false
    this.clearPendingAttachment()
  }

  toggleChatInfo() {
    this.isChatInfoOpen = !this.isChatInfoOpen
    if (this.isChatInfoOpen) {
      this.closeMessageSearch()
    }
  }

  closeChatInfo() {
    this.isChatInfoOpen = false
  }

  openMessageSearch() {
    this.isMessageSearchOpen = true
    this.messageSearchQuery = ''
    this.isChatInfoOpen = false
  }

  closeMessageSearch() {
    this.isMessageSearchOpen = false
    this.messageSearchQuery = ''
    this.activeSearchIndex = 0
  }

  setMessageSearchQuery(query: string) {
    this.messageSearchQuery = query
    this.activeSearchIndex = 0
  }

  setActiveSearchIndex(index: number) {
    this.activeSearchIndex = index
  }

  setReplyToMessage(messageId: string | null) {
    this.replyToMessageId = messageId
    this.editingMessageId = null
  }

  clearReply() {
    this.replyToMessageId = null
  }

  setEditingMessage(messageId: string | null) {
    this.editingMessageId = messageId
    this.replyToMessageId = null
  }

  clearEditing() {
    this.editingMessageId = null
  }

  setScrollToMessage(messageId: string | null) {
    this.scrollToMessageId = messageId
  }

  setPrimaryColor(color: string) {
    this.primaryColor = color
  }

  setPendingAttachment(type: AttachmentType, file: File) {
    this.clearPendingAttachment()
    this.pendingAttachment = {
      type,
      file,
      objectUrl: URL.createObjectURL(file),
    }
  }

  clearPendingAttachment() {
    if (this.pendingAttachment) {
      URL.revokeObjectURL(this.pendingAttachment.objectUrl)
      this.pendingAttachment = null
    }
  }

  setDraft(chatId: string, html: string) {
    if (html) {
      this.drafts.set(chatId, html)
    } else {
      this.drafts.delete(chatId)
    }
  }

  getDraft(chatId: string): string | undefined {
    return this.drafts.get(chatId)
  }

  clearDraft(chatId: string) {
    this.drafts.delete(chatId)
  }
}
